/** The one fill colour scheme of the app (rule U-22); installFillColours makes the CSS variables `--fill-0` to `--fill-5`. */
export interface FillBand {
  /** The lowest percent of the band; the band goes to the `from` of the next band. */
  from: number;
  label: string;
  dark: string;
  /** The colour on the light theme: darker where the dark colour has less than 3:1 on white. */
  light: string;
}

export const FILL_BANDS: readonly FillBand[] = [
  { from: 0, label: "Under 25%", dark: "#64748b", light: "#64748b" },
  { from: 25, label: "25–50%", dark: "#38bdf8", light: "#0284c7" },
  { from: 50, label: "50–75%", dark: "#facc15", light: "#a16207" },
  { from: 75, label: "75–90%", dark: "#fb923c", light: "#ea580c" },
  { from: 90, label: "90–99%", dark: "#ef4444", light: "#ef4444" },
  { from: 100, label: "Full or over", dark: "#d946ef", light: "#d946ef" },
];

export const OVER_BAND = FILL_BANDS.length - 1;

/** The band of a fill percent: 0 is under 25%, 5 is full or more. */
export function fillBand(pct: number): number {
  let found = 0;
  FILL_BANDS.forEach((band, index) => {
    if (pct + 1e-9 >= band.from) found = index;
  });
  return found;
}

/** The band of a fill share, where 1 is full; the rounded percent, so the dot and "About N% full" agree. */
export const shareBand = (share: number) => fillBand(Math.round(share * 100));

export const fillVar = (band: number) => `var(--fill-${band})`;

export interface DotLook {
  /** A share of the container, or null when there is no estimate. */
  share: number | null;
  /** On no route at or over the high share: a dashed white outer ring. */
  loose?: boolean;
  picked?: boolean;
  faded?: boolean;
  small?: boolean;
  /** A stop number inside a larger dot; the fill rises behind it. */
  number?: number | string | null;
  /** A rough geocode that a person must check: a dashed square ring. */
  check?: boolean;
  /** A late stop or a sensor alert: a dashed round ring. */
  alert?: boolean;
  /** A finished stop of today: pale. */
  done?: boolean;
}

/** The class and the style of a level dot: filled from the bottom to the fill, with an outline in the band colour. */
export function dotLook(look: DotLook): { className: string; level: string; band: number | null } {
  const band = look.share === null ? null : shareBand(look.share);
  const classes = ["level-dot", band === null ? "none" : `b${band}`];
  if (band === OVER_BAND) classes.push("over");
  if (look.loose) classes.push("loose");
  if (look.picked) classes.push("picked");
  if (look.faded) classes.push("faded");
  if (look.small) classes.push("small");
  if (look.number != null) classes.push("numbered");
  if (look.check) classes.push("check");
  if (look.alert) classes.push("alert");
  if (look.done) classes.push("done");
  const level = look.share === null ? 0 : Math.min(Math.max(look.share, 0), 1);
  return { className: classes.join(" "), level: `${Math.round(level * 100)}%`, band };
}

/** A level dot as a DOM element, for the content of a map marker. */
export function levelDot(look: DotLook): HTMLElement {
  const { className, level } = dotLook(look);
  const element = document.createElement("div");
  element.className = className;
  element.style.setProperty("--level", level);
  element.textContent = dotText(look);
  return element;
}

/** The text inside a dot: the stop number, else "?" when there is no estimate. */
export const dotText = (look: Pick<DotLook, "share" | "number">) => (look.number != null ? String(look.number) : look.share === null ? "?" : "");

/** The percent in the fill words: it stops at 100%, but the colour still shows more than full. */
const wordsPct = (share: number) => Math.round(Math.min(Math.max(share, 0), 1) * 100);

/** "About 62% full", or "No estimate" when the API gives no share. */
export const fillWords = (share: number | null | undefined) => (share == null ? "No estimate" : `About ${wordsPct(share)}% full`);

/** The name of an account on each map (rule U-23): "D-0038 Demo Diner 0038". */
export const accountName = (number: string | null | undefined, name: string | null | undefined) => [number, name].filter(Boolean).join(" ") || "Account";

export const FLOAT_PCT = 75;

/** Rules S-1 and S-7: a float switch only knows if the oil is above or below the float. */
export const floatWords = (full: boolean, floatPct: number | null | undefined) => `${full ? "Above" : "Below"} the float (${floatPct ?? FLOAT_PCT}%)`;

/** A fresh float reading wins over the estimate: the dot stays on the side of the float that the reading gives. */
export function floatShare(full: boolean, floatPct: number | null | undefined, estimate: number | null | undefined): number | null {
  const line = (floatPct ?? FLOAT_PCT) / 100;
  if (full) return Math.max(estimate ?? line, line);
  return estimate == null ? null : Math.min(estimate, line - 0.01);
}

/** The side of the float of a Home tank point: true above, false below, null for no float report. */
export function tankSide(tank: { state: string; float_pct?: number | null; level?: string | null }): boolean | null {
  if (tank.state === "full") return true;
  if (tank.float_pct == null || (tank.level !== "full" && tank.level !== "not_full")) return null;
  return tank.level === "full";
}

/** True when the estimate is strictly on the side of the float: a share that the API held at the line is not an estimate. */
export function floatAgrees(full: boolean, floatPct: number | null | undefined, estimate: number | null | undefined): boolean {
  if (estimate == null) return false;
  const line = (floatPct ?? FLOAT_PCT) / 100;
  return full ? estimate > line : estimate < line - 0.01;
}

/** A fresh float report of an account (rules S-1, S-8): above the float or not, and its height in percent. */
export interface FloatSide {
  full: boolean;
  pct: number;
}

/** The fill words of a dot: the float words when a fresh float report holds the fill, with the estimate only when it is strictly on that side. */
export function sideWords(side: FloatSide | null | undefined, share: number | null | undefined): string {
  if (!side) return fillWords(share);
  const said = floatWords(side.full, side.pct);
  return floatAgrees(side.full, side.pct, share) ? `${said} · estimate about ${wordsPct(share ?? 0)}%` : said;
}

/** The fill words of the forecast: the float words, then the estimate or the share that the float holds. */
export function forecastWords(side: FloatSide | null | undefined, share: number | null | undefined): string {
  if (!side || share == null || floatAgrees(side.full, side.pct, share)) return sideWords(side, share);
  return `${floatWords(side.full, side.pct)} · held at ${wordsPct(share)}% for the forecast`;
}

/** The hover text of a dot on each map: the given parts, then the fill. */
export const dotTitle = (parts: (string | null | undefined | false)[], share: number | null | undefined, side?: FloatSide | null) => [...parts.filter(Boolean), sideWords(side, share)].join(" · ");

/** The CSS variables of the scheme for the dark theme and the light theme. */
export function fillCss(): string {
  const vars = (theme: "dark" | "light") => FILL_BANDS.map((band, index) => `--fill-${index}: ${band[theme]};`).join(" ");
  return `:root { ${vars("dark")} }\n:root[data-theme="light"] { ${vars("light")} }`;
}

export function installFillColours(): void {
  const style = document.createElement("style");
  style.dataset.fill = "";
  style.textContent = fillCss();
  document.head.append(style);
}
